import * as React from 'react';
import { useMemo, useState } from 'react';
import type { BankingDetails } from '../../../../graphql/generated/graphql';
import { EditorProps } from '../types/editor-props';
import { EditBankingDetailsModal } from '../../../../modules/modals/EditBankingDetailsModal';
import { BankingDetailsFormatter } from '../formatters/BankingDetailsFormatter';

const isBankingDetails = (value: unknown): value is BankingDetails =>
  typeof value === 'object' && value !== null;

/**
 * Editor for structured BankingDetails values.
 * Renders the current details inline and opens an EditBankingDetailsModal.
 * On save, awaits `onCommit` so a rejected upstream save keeps the modal open
 * and surfaces the failure (instead of closing silently).
 *
 * Strips any `bankingDetails.` prefix from incoming issue paths so the modal
 * can map issues to specific fields by short key (e.g. `swift`, `iban`).
 */
export function BankingDetailsEditor({ value, onChange, onCommit, onCancel, errors }: EditorProps) {
  const [open, setOpen] = useState(true);

  const bankingValue = isBankingDetails(value) ? (value as BankingDetails) : null;

  const formErrors = useMemo(
    () =>
      (errors ?? []).map(issue => ({
        ...issue,
        path:
          issue.path && issue.path.startsWith('bankingDetails.')
            ? issue.path.slice('bankingDetails.'.length)
            : (issue.path ?? '')
      })),
    [errors]
  );

  const handleSave = async (payload: Partial<BankingDetails>) => {
    const next: BankingDetails = {
      bankAccount: payload.bankAccount || null,
      bankName: payload.bankName || null,
      iban: payload.iban || null,
      swift: payload.swift || null,
      bankCode: payload.bankCode || null,
      varSymb: payload.varSymb,
      constSymb: payload.constSymb,
      specSymb: payload.specSymb
    };
    if (onCommit) {
      await onCommit(next);
    } else {
      onChange(next);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onCancel?.();
  };

  return (
    <>
      <div className='w-full'>
        <BankingDetailsFormatter value={bankingValue} />
      </div>
      <EditBankingDetailsModal
        open={open}
        bankingDetails={bankingValue}
        onClose={handleClose}
        onSave={handleSave}
        errors={formErrors}
      />
    </>
  );
}

(BankingDetailsEditor as React.FC).displayName = 'BankingDetailsEditor';
